import { useEffect, useRef, useState } from "react";
import { HeartIcon, PaperPlaneIcon, TextBubbleIcon, MenuDotsIcon, CameraIcon, GraphIcon, HashtagIcon, CloseIcon, SearchIcon } from "../../util/Icons";

import { useUserContext } from "../../../context/UserProvider";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  setDoc,
  startAfter,
  updateDoc,
  where,
  getAggregateFromServer,
  sum,
} from "firebase/firestore";
import { db, storage } from "../../../firebase";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import Spinner from "../../util/Spinner";
import ProfilePicture from "../../util/ProfilePicture";
import confirmPopup from "../../util/ConfirmPopup";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import DropDownMenu from "../../util/DropDownMenu";
import { usePopUpContext } from "../../../context/PopUpProvider";
import SocialFeedTutorial from "../../Tutorials/SocialFeedTutorial";
import SingleComment from "./SingleComment";
import PostInput from "./PostInput";
import SinglePost from "./SinglePost";
import { getActiveTeamFeedFilters } from "./feedFilterUtils";
import { normalizeSocialProfileSearchText } from "./socialProfileSearchUtils";

const DEFAULT_TAG_COLOR_STYLES = {
  bg: "bg-indigo-500",
  border: "border-indigo-500",
  text: "text-indigo-600",
  lightBg: "bg-indigo-50",
  lightBorder: "border-indigo-200",
};

function normalizeHashTagOptions(optionsData) {
  const options = Object.entries(optionsData || {})
    .filter(([name, value]) => {
      return typeof name === "string" && name.length > 0 && typeof value === "object" && value !== null;
    })
    .map(([name, value]) => {
      const order = Number.isFinite(value.order) ? value.order : Number.MAX_SAFE_INTEGER;
      return {
        name,
        order,
        styles: {
          bg: value.bg || DEFAULT_TAG_COLOR_STYLES.bg,
          border: value.border || DEFAULT_TAG_COLOR_STYLES.border,
          text: value.text || DEFAULT_TAG_COLOR_STYLES.text,
          lightBg: value.lightBg || DEFAULT_TAG_COLOR_STYLES.lightBg,
          lightBorder: value.lightBorder || DEFAULT_TAG_COLOR_STYLES.lightBorder,
        },
      };
    })
  return options.sort((a, b) => {
      if (a.order === b.order) return a.name.localeCompare(b.name);
      return a.order - b.order;
    });
}

const SocialFeed = () => {
  const { user } = useUserContext();
  const { userDetails, userRaces, canSocialFeed, userData, socialFeedEndDate } = useUserContext();
  const { showPopUp } = usePopUpContext();
  const { postId } = useParams();
  const navigate = useNavigate();
  const [activePost, setActivePost] = useState(null);
  const [postFeed, setPostFeed] = useState(null);
  const [lastPost, setLastPost] = useState(null);
  const [feedFilters, setFeedFilters] = useState([]);
  const [teamFeedFilters, setTeamFeedFilters] = useState([]);
  const [filterByID] = useState(false);
  const [loadingMore, setLoadingMore] = useState(true);
  const [seenLastPost, setSeenLastPost] = useState(false);
  const [hashTagOptions, setHashTagOptions] = useState([]);
  const [peopleSearchOpen, setPeopleSearchOpen] = useState(false);
  const [peopleSearchTerm, setPeopleSearchTerm] = useState("");
  const [peopleSearchResults, setPeopleSearchResults] = useState([]);
  const [peopleSearchLoading, setPeopleSearchLoading] = useState(false);
  const postLimit = 4;
  const hashTagNames = hashTagOptions.map((option) => option.name);
  const teamFilterOptions = getActiveTeamFeedFilters(userRaces);
  const queryLoading = useRef(false);
  const peopleSearchInputRef = useRef(null);
  const peopleSearchRequestId = useRef(0);

  // Social feed tutorial
  useEffect(() => {
    async function showFeedTutorial() {
      if (!userData?.tutorials?.socialFeed) {
        const popUpState = await showPopUp(<SocialFeedTutorial />);
        if (popUpState !== null) {
          console.log("Social feed tutorial done", popUpState);
          // Update tutorial variable only 1 field inside tutorials
          await updateDoc(doc(db, "users", user.uid), { "tutorials.socialFeed": true });
        }
      }
    }
    showFeedTutorial();
  }, []);

  useEffect(() => {
    async function fetchHashTagOptions() {
      try {
        const optionsDoc = await getDoc(doc(db, "options", "hashTagNames"));
        if (!optionsDoc.exists()) {
          setHashTagOptions([]);
          return;
        }
        const normalizedOptions = normalizeHashTagOptions(optionsDoc.data());
        setHashTagOptions(normalizedOptions);
      } catch (error) {
        console.error("Failed to fetch hashtag options", error?.message || error);
        setHashTagOptions([]);
      }
    }
    fetchHashTagOptions();
  }, []);

  useEffect(() => {
    if (!canSocialFeed) return;
    setActivePost(null);
    if (!!postId) {
      setActivePost(postId);
    }
  }, [postId]);
  // const seenLastPostRef = useRef();
  // const loadingMoreRef = useRef();
  // const lastPostRef = useRef();
  // useEffect(() => {
  //   loadingMoreRef.current = loadingMore;
  //   lastPostRef.current = lastPost;
  //   seenLastPostRef.current = seenLastPost;
  // }, [loadingMore, lastPost, seenLastPost]);
  useEffect(() => {
    if (loadingMore || seenLastPost || !canSocialFeed) return;
    // console.warn("updating scroll listener");
    function handleScroll() {
      let margin = 200;
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      if (!loadingMore && !seenLastPost && scrollTop + clientHeight >= scrollHeight - margin) {
        console.log("scroll bot");
        queryFeed(lastPost);
        setLoadingMore(true);
      }
    }
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loadingMore, seenLastPost, lastPost]);
  useEffect(() => {
    if (!canSocialFeed) return;
    // if (postFeed) {
    //     console.log("Feed already there");
    //     return;
    // }
    // console.log("Querying social feed");

    setSeenLastPost(false);
    queryFeed();
    console.log(
      `Filter id: %c${filterByID} %cFilters: %c${feedFilters.toString()} %cTeam filters: %c${teamFeedFilters.toString()}`,
      `color: ${filterByID ? "limegreen" : "red"}`,
      "color: inherit",
      "color: yellow",
      "color: inherit",
      "color: cyan"
    );
  }, [feedFilters, teamFeedFilters, filterByID]);

  useEffect(() => {
    if (!peopleSearchOpen) {
      setPeopleSearchTerm("");
      setPeopleSearchResults([]);
      setPeopleSearchLoading(false);
      return;
    }

    setTimeout(() => peopleSearchInputRef.current?.focus(), 0);
  }, [peopleSearchOpen]);

  useEffect(() => {
    if (!peopleSearchOpen) return;

    const normalizedSearchTerm = normalizeSocialProfileSearchText(peopleSearchTerm);
    const requestId = peopleSearchRequestId.current + 1;
    peopleSearchRequestId.current = requestId;

    if (normalizedSearchTerm.length < 2) {
      setPeopleSearchResults([]);
      setPeopleSearchLoading(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setPeopleSearchLoading(true);

      try {
        const peopleQuery = query(
          collection(db, "social_profile"),
          where("searchPrefixes", "array-contains", normalizedSearchTerm),
          limit(8)
        );
        const peopleSnap = await getDocs(peopleQuery);
        const results = peopleSnap.docs
          .map((profileDoc) => ({ id: profileDoc.id, ...profileDoc.data() }))
          .filter((profile) => profile?.uid && profile?.authorName)
          .sort((a, b) => {
            const aLatestPost = Number(a?.latestPostAt) || 0;
            const bLatestPost = Number(b?.latestPostAt) || 0;
            if (aLatestPost !== bLatestPost) return bLatestPost - aLatestPost;
            return String(a.authorName || "").localeCompare(String(b.authorName || ""));
          });

        if (peopleSearchRequestId.current === requestId) {
          setPeopleSearchResults(results);
        }
      } catch (error) {
        console.error("Failed to search social profiles", error);
        if (peopleSearchRequestId.current === requestId) {
          setPeopleSearchResults([]);
        }
      } finally {
        if (peopleSearchRequestId.current === requestId) {
          setPeopleSearchLoading(false);
        }
      }
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [peopleSearchOpen, peopleSearchTerm]);

  async function queryFeed(fromPost) {
    // if (seenLastPost) return;
    if (queryLoading.current) return;
    queryLoading.current = true;
    try {
      console.log("Query feed", fromPost ? "load more" : "first load");
      let whereFilter = [];
      if (teamFeedFilters.length > 0) {
        whereFilter = [where("feedFilterKeys", "array-contains-any", teamFeedFilters)];
      } else if (feedFilters.length > 0) {
        whereFilter = [where("hashtags", "array-contains-any", feedFilters)];
      }
      if (filterByID) {
        whereFilter.push(where("authorID", "==", user.uid));
      }
      if (!fromPost) {
        const q = query(collection(db, "social_feed"), orderBy("epochTime", "desc"), limit(postLimit), ...whereFilter);
        const docsSnap = await getDocs(q);
        //   if (docsSnap.size > 0) {
        let docsData = [];
        docsSnap.forEach((doc) => {
          let data = doc.data();
          data.id = doc.id;
          docsData.push(data);
        });
        //   console.log(docsData);
        setLastPost(docsSnap.docs[docsSnap.docs.length - 1]);
        setPostFeed(docsData);
        setLoadingMore(false);
        //   }
      } else {
        const q = query(collection(db, "social_feed"), orderBy("epochTime", "desc"), startAfter(fromPost), limit(postLimit), ...whereFilter);
        const docsSnap = await getDocs(q);
        if (docsSnap.size > 0) {
          let docsData = [];
          docsSnap.forEach((doc) => {
            let data = doc.data();
            data.id = doc.id;
            docsData.push(data);
          });
          //   console.log(docsData);
          setLastPost(docsSnap.docs[docsSnap.docs.length - 1]);
          setPostFeed((prev) => [...prev, ...docsData]);
          setLoadingMore(false);
        }
        if (docsSnap.size === 0) {
          setSeenLastPost(true);
          setLoadingMore(false);
        }
      }
    } catch (error) {
      console.error("Failed to query social feed", error);
      setLoadingMore(false);
    } finally {
      queryLoading.current = false;
    }
  }
  /**
   * Delets a post by id inside social_feed collection
   * @param {{id: string}} post Post to be deleted
   */
  async function deletePost(post) {
    let confirm = await confirmPopup({ message: "Delte this post?", textY: "Delete", textN: "Nevermind" });
    if (!confirm) return;
    const postID = post?.id;
    if (!postID) return;
    if (post?.picturesRefs?.length > 0) {
      console.log("Deleting pictures");
      for (const p of post.picturesRefs) {
        let picRef = ref(storage, p);
        await deleteObject(picRef)
          .then(() => {
            console.log("Deleted picture", p);
          })
          .catch((err) => {
            console.error(err.message);
          });
      }
    }
    console.log("Deleting Post");
    deleteDoc(doc(db, "social_feed", postID))
      .then(() => {
        let delIndex = postFeed.findIndex((post) => {
          return post?.id === postID;
        });
        if (delIndex !== -1) {
          let prev = postFeed;
          prev.splice(delIndex, 1);
          setPostFeed([...prev]);
        }
        console.log("Successful delete");
      })
      .catch((err) => {
        console.warn(err.message);
      });
  }
  async function editPost(post, message) {
    const postID = post?.id;
    if (!postID) return;
    if (user.uid !== post?.authorID) return;

    const editTime = Date.now();
    const postPatch = {
      message,
      editedAt: editTime,
      updatedAt: new Date(editTime).toISOString(),
    };

    await updateDoc(doc(db, "social_feed", postID), postPatch);
    setPostFeed((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((item) => {
        if (item.id !== postID) return item;
        return { ...item, ...postPatch };
      });
    });

    return postPatch;
  }
  return (
    <>
      <div className="relative min-w-0 w-full">
        {socialFeedEndDate && <SocialFeedCountDownBanner />}
        <div className="pt-2">
          <PostInput addPost={setPostFeed} categories={hashTagNames}></PostInput>
        </div>
        <div className="flex flex-col md:px-3 py-2 pt-4">
          <div className="flex items-center gap-2 w-full">
            <div className="relative flex items-center flex-shrink-0">
              <button
                type="button"
                className={`h-8 w-8 rounded-full border flex items-center justify-center transition-colors active:scale-95 ${peopleSearchOpen
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-500 border-slate-200 hover:bg-slate-100"
                  }`}
                aria-label={peopleSearchOpen ? "Close people search" : "Search people"}
                title="Search people"
                onClick={() => setPeopleSearchOpen((prev) => !prev)}
              >
                <SearchIcon size={16} />
              </button>
              {peopleSearchOpen && (
                <div className="absolute left-0 top-10 z-30 w-[min(18rem,calc(100vw-2rem))]">
                  <div className="bg-white border border-slate-200 rounded-lg shadow-xl p-2">
                    <div className="flex items-center gap-2">
                      <input
                        ref={peopleSearchInputRef}
                        type="search"
                        value={peopleSearchTerm}
                        onChange={(event) => setPeopleSearchTerm(event.target.value)}
                        placeholder="Search people"
                        className="min-w-0 flex-1 border border-slate-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      />
                      <button
                        type="button"
                        aria-label="Close people search"
                        className="h-8 w-8 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
                        onClick={() => setPeopleSearchOpen(false)}
                      >
                        <CloseIcon size={14} />
                      </button>
                    </div>
                    <PeopleSearchResults
                      loading={peopleSearchLoading}
                      results={peopleSearchResults}
                      searchTerm={peopleSearchTerm}
                      onSelect={(profile) => {
                        setPeopleSearchOpen(false);
                        navigate(`/profile/${profile.uid}`);
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
            <p className="m-0 whitespace-nowrap text-sm font-medium text-slate-600">Filter By:</p>
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar flex-1" style={{ scrollbarWidth: "none" }}>
              {teamFilterOptions.map((teamFilter) => {
                return (
                  <TeamFilterButton
                    key={teamFilter.key}
                    filter={teamFilter}
                    teamFeedFilters={teamFeedFilters}
                    setTeamFeedFilters={setTeamFeedFilters}
                    setFeedFilters={setFeedFilters}
                  />
                );
              })}
              {hashTagOptions.map((tagOption) => {
                return (
                  <FilterButton
                    key={tagOption.name}
                    filter={tagOption.name}
                    colorStyles={tagOption.styles}
                    setFeedFilters={setFeedFilters}
                    feedFilters={feedFilters}
                    setTeamFeedFilters={setTeamFeedFilters}
                  />
                );
              })}
            </div>
          </div>
          {postFeed !== null &&
            postFeed?.length > 0 &&
            postFeed.map((post) => {
              return (
                <SinglePost
                  key={post?.id}
                  post={post}
                  deletePost={deletePost}
                  editPost={editPost}
                  hashtagOptions={hashTagOptions}
                />
              );
            })}
          {postFeed?.length === 0 && <p>No posts on this filter</p>}
          {/* <button
          onClick={() => {
            if (lastPost !== null) {
              queryFeed(lastPost);
              }
              }}
              >
              Load More
              </button> */}
          {loadingMore && <Spinner size={64}></Spinner>}
          {seenLastPost && <p className="text-center mb-0">No more posts...</p>}
        </div>
      </div>
      {activePost && (
        <ActivePost
          postID={activePost}
          deletePost={deletePost}
          editPost={editPost}
          hashtagOptions={hashTagOptions}
        />
      )}
    </>
  );
};

function PeopleSearchResults({ loading, results, searchTerm, onSelect }) {
  const normalizedSearchTerm = normalizeSocialProfileSearchText(searchTerm);

  if (normalizedSearchTerm.length < 2) {
    return <p className="text-xs text-slate-400 mt-2 mb-0 px-1">Type at least 2 characters</p>;
  }

  if (loading) {
    return <p className="text-xs text-slate-400 mt-2 mb-0 px-1">Searching...</p>;
  }

  if (results.length === 0) {
    return <p className="text-xs text-slate-400 mt-2 mb-0 px-1">No people found</p>;
  }

  return (
    <div className="mt-2 max-h-72 overflow-y-auto">
      {results.map((profile) => (
        <button
          type="button"
          key={profile.uid}
          className="w-full grid grid-cols-[32px,1fr] items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-slate-50 transition-colors"
          onClick={() => onSelect(profile)}
        >
          <ProfilePicture userID={profile.uid} name={profile.authorName} className="!w-8 !h-8 text-xs" />
          <span className="min-w-0">
            <span className="block text-sm font-medium text-slate-700 truncate">{profile.authorName}</span>
            {profile.location && <span className="block text-xs text-slate-400 truncate">{profile.location}</span>}
          </span>
        </button>
      ))}
    </div>
  );
}

function ActivePost({ postID, deletePost, editPost, hashtagOptions }) {
  const [postData, setPostData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    (async () => {
      let postDoc = await getDoc(doc(db, "social_feed", postID));
      if (postDoc.exists()) {
        let newData = postDoc.data();
        newData.id = postID;
        setPostData(newData);
      } else {
        navigate("/");
      }
    })();
  }, []);
  async function handleEditPost(post, message) {
    const postPatch = await editPost(post, message);
    if (postPatch) {
      setPostData((prev) => ({ ...prev, ...postPatch }));
    }
    return postPatch;
  }
  //Get post data if any
  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-slate-900 bg-opacity-50 backdrop-blur-sm flex items-start md:items-center justify-center md:p-4"
      onClick={(e) => {
        navigate("/");
      }}
    >
      {postData && (
        <div
          className="bg-white md:rounded-xl w-full max-w-xl mx-auto shadow-2xl overflow-hidden flex flex-col h-full md:h-auto md:max-h-[90vh]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative p-2 flex items-center justify-center flex-shrink-0 bg-white z-10">
            <h3 className="font-semibold text-lg m-0 text-slate-800">{postData.authorName?.split(" ")[0]}'s Post</h3>
            <button
              onClick={() => navigate("/")}
              className="absolute right-4 p-2 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors flex items-center justify-center group"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-slate-500 group-hover:text-slate-700"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          {/* Scrollable Content Container */}
          <div className="overflow-y-auto p-2 bg-white flex-1">
            <SinglePost
              key={postID}
              post={postData}
              deletePost={deletePost}
              editPost={handleEditPost}
              hashtagOptions={hashtagOptions}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ProgressComponent({ children, imgUrl, raceName, legName, progress, teamProgress, distance }) {
  const totalProgress = teamProgress || progress || 0;
  return (
    <div className="relative w-full flex gap-3 items-center px-2 mt- mb-3">
      <img src={imgUrl} alt="" className="h-[32px] w-[32px]" />
      <div className="w-full">
        <p className="m-0 font-semibold">{raceName}</p>
        <p className="m-0 font-semibold text-sm">{legName}</p>
        <div className="flex">
          <span className="text-sm">
            Progress: {totalProgress?.toFixed(2)}km / {distance}km {((totalProgress / distance) * 100).toFixed(2)}%
          </span>
        </div>
        <div className="bg-slate-500 w-full h-2">
          <div className="bg-green-500 h-2" style={{ width: `${(totalProgress / distance) * 100}%` }}></div>
        </div>
      </div>
      {children}
    </div>
  );
}

function FilterButton({ filter, feedFilters, setFeedFilters, setTeamFeedFilters, colorStyles = DEFAULT_TAG_COLOR_STYLES }) {
  const isSelected = feedFilters.includes(filter);

  return (
    <button
      className={`px-3 py-1 border rounded-full inline-block font-medium text-xs transition-all duration-200 active:scale-95 flex-shrink-0
        ${isSelected
          ? `${colorStyles.bg} text-white ${colorStyles.border} shadow-sm`
          : `bg-white ${colorStyles.text} ${colorStyles.lightBorder} hover:${colorStyles.lightBg}`
        }`}
      onClick={() => {
        setTeamFeedFilters([]);
        if (feedFilters.indexOf(filter) !== -1) {
          setFeedFilters([]);
          return;
        }
        setFeedFilters([filter]);
      }}
    >
      #{filter}
    </button>
  );
}

function TeamFilterButton({ filter, teamFeedFilters, setTeamFeedFilters, setFeedFilters }) {
  const isSelected = teamFeedFilters.includes(filter.key);

  return (
    <button
      className={`px-3 py-1 border rounded-full inline-block font-medium text-xs transition-all duration-200 active:scale-95 flex-shrink-0
        ${isSelected
          ? "bg-slate-900 text-white border-slate-900 shadow-sm"
          : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
        }`}
      title={filter.raceName ? `${filter.raceName} team` : "Team"}
      onClick={() => {
        setFeedFilters([]);
        if (isSelected) {
          setTeamFeedFilters([]);
          return;
        }
        setTeamFeedFilters([filter.key]);
      }}
    >
      {filter.label}
    </button>
  );
}

function WhoLiked({ count, likeCollection }) {
  const [latestLiker, setLatestLiker] = useState("...")

  useEffect(() => {
    async function getLatestLiker() {
      if (!likeCollection || count <= 0) return
      try {
        const q = query(likeCollection, orderBy("epochTime", "desc"), limit(1))
        const snap = await getDocs(q)
        if (!snap.empty) {
          const latestDoc = snap.docs[0].data()
          setLatestLiker(latestDoc.name || latestDoc.userName)
        }
      } catch (err) {
        console.error("Error fetching latest liker", likeCollection, err.message)
      }
    }
    getLatestLiker()
  }, [count, likeCollection])

  if (count <= 0) return null
  return <div className="flex text-sm select-none flex-wrap gap-x-1">

    {/* {count > 1 && */}
    <DropDownMenu
      trigger={({ ref, onClick, open }) => {
        return <button ref={ref} onClick={() => {
          /*if (count > 1 && !open) This if we want to prevent the all likes popup if there's only 1 like*/
          if (!open) { onClick() }
        }}
          className="text-slate-500 italic cursor-pointer text-xs hover:underline"
        >
          <p className="m-0">
            Liked by <span>{latestLiker?.split(" ")[0]}</span>{count > 1 && <span> and {count - 1} others</span>}
          </p>
        </button>
      }}
      closeOnClick={false}
    >
      <div className="fixed inset-0 flex justify-center items-center bg-slate-900 bg-opacity-50 backdrop-filter" data-close>

        <AllLikes likeCollection={likeCollection} />
      </div>
    </DropDownMenu>
    {/* } */}
  </div>
}

function AllLikes({ likeCollection }) {
  const [likes, setLikes] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const READS_LIMIT = 5

  // Function to fetch likes
  const fetchLikes = async (isFirstLoad = false) => {
    if (!likeCollection) return;

    setLoading(true);
    try {
      // 1. Build the query
      let q;
      if (isFirstLoad) {
        // First page
        q = query(likeCollection, orderBy("epochTime", "desc"), limit(READS_LIMIT));
      } else {
        // Next pages (start after the last doc we saw)
        q = query(likeCollection, orderBy("epochTime", "desc"), startAfter(lastDoc), limit(READS_LIMIT));
      }

      const snap = await getDocs(q);

      if (!snap.empty) {
        const newLikes = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // 2. Update state
        setLikes(prev => isFirstLoad ? newLikes : [...prev, ...newLikes]);
        setLastDoc(snap.docs[snap.docs.length - 1]);

        // 3. Check if we hit the end
        if (snap.docs.length < READS_LIMIT) {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error fetching likes:", err);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    setLikes([]);
    setHasMore(true);
    fetchLikes(true);
  }, [likeCollection]);

  return (
    <div className="border rounded-lg p-4 bg-white shadow-sm max-w-md w-full max-h-full flex flex-col">
      <div className="flex justify-end mb-0">
        <div data-close className="cursor-pointer p-1 hover:bg-slate-100 rounded-full transition-colors scale-75 origin-right">
          <div className="pointer-events-none">
            <CloseIcon size={14} />
          </div>
        </div>
      </div>

      <ul className="space-y-1 pb-2 select-text h-full overflow-y-auto">
        {likes.map((like) => (
          <li key={like.id}>
            <Link to={`/profile/${like.uid || like.authorId}`} className="text-gray-700 py-1.5 grid grid-cols-[20px,1fr] items-center gap-2 hover:bg-slate-50 px-2 rounded-md transition-colors no-underline hover:no-underline">
              <HeartIcon size={16} color={"#ef4444"} />
              <span className="text-sm font-medium">
                {like.name || like.userName || "Anonymous"}
              </span>
            </Link>
          </li>
        ))}
      </ul>

      {loading && <p className="text-sm text-gray-400 italic text-left py-2 px-2">Loading...</p>}

      {!loading && hasMore && (
        <button
          onClick={() => fetchLikes(false)}
          className="py-2 text-left text-slate-500 hover:bg-slate-50 px-2 rounded font-medium transition-colors ml-[20px]"
        >
          Show more
        </button>
      )}

      {!hasMore && likes.length > 0 && (
        <p className="text-left text-xs text-gray-400 mt-2 italic px-2">No more likes to show</p>
      )}
    </div>
  );
}

function SocialFeedCountDownBanner() {
  const { socialFeedEndDate } = useUserContext();
  const [showOverlay, setShowOverlay] = useState(false);

  const remainingTime = socialFeedEndDate - Date.now();
  const [countDown, setCountDown] = useState({
    d: Math.floor(remainingTime / (1000 * 60 * 60 * 24)),
    h: Math.floor((remainingTime / (1000 * 60 * 60)) % 24),
    m: Math.floor((remainingTime / (1000 * 60)) % 60),
    s: Math.floor((remainingTime / 1000) % 60),
  });

  useEffect(() => {
    function liveCountDown() {
      const remainingTime = socialFeedEndDate - Date.now();
      if (remainingTime <= 0) {
        setCountDown({ d: 0, h: 0, m: 0, s: 0 });
        return;
      }
      setCountDown({
        d: Math.floor(remainingTime / (1000 * 60 * 60 * 24)),
        h: Math.floor((remainingTime / (1000 * 60 * 60)) % 24),
        m: Math.floor((remainingTime / (1000 * 60)) % 60),
        s: Math.floor((remainingTime / 1000) % 60),
      });
    }
    const interval = setInterval(liveCountDown, 1000);
    return () => clearInterval(interval);
  }, [socialFeedEndDate]);

  const CountDownLine = () => (
    <span className="font-mono font-bold">
      {countDown.d > 0 && <span>{countDown.d}d </span>}
      {countDown.h > 0 && <span>{countDown.h}h </span>}
      {countDown.m > 0 && <span>{countDown.m}m </span>}

    </span>
  );

  return (
    <>
      {/* Banner - 2 column layout */}
      <div
        className="mt-3 px-4 py-3 rounded-2xl overflow-hidden"
        style={{ background: 'linear-gradient(to right, #e10c0c, #8b44a2, #0103ff)' }}
      >
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-3 items-center">
          {/* Left column - Message (2/3 width) */}
          <div className="text-white text-xs md:text-sm text-center md:text-left">
            Your complementary access to the feed will end in <CountDownLine />
          </div>

          {/* Right column - Button (1/3 width) */}
          <div className="flex justify-center md:justify-end">
            <button
              onClick={() => setShowOverlay(true)}
              className="px-4 py-1 text-white rounded-full text-sm shadow-md cursor-pointer border-none"
              style={{
                backgroundColor: '#f97316',
                transition: 'background-color 150ms ease-in-out',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ea580c'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f97316'}
            >
              Learn More
            </button>
          </div>
        </div>
      </div>

      {/* Overlay Popup */}
      {showOverlay && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900 bg-opacity-50 backdrop-blur-sm p-4"
          onClick={() => setShowOverlay(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowOverlay(false)}
              className="absolute top-4 right-4 p-2 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors flex items-center justify-center cursor-pointer border-none"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-slate-500"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            {/* Content */}
            <h3 className="text-xl font-bold text-slate-800 mb-4 pr-8">Social Feed Access</h3>

            <p className="text-slate-600 mb-4">
              When you register for a challenge you get free access to the social feed, plus 30 days extra of complementary access.
            </p>

            <p className="text-slate-600 mb-4">
              If you want to keep access and get your next 3 challenges for free, we invite you to join the Momentum Membership.{' '}
              <a href="https://mychallengecanada.ca/momentum/" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold hover:underline">
                Learn more here
              </a>
            </p>
          </div>
        </div>
      )}
    </>
  );
}



/**
 * Returns a human-readable time difference string like "5 minutes ago". AI
 *
 * @param {number} fromEpoch - The earlier timestamp in milliseconds (epoch time).
 * @param {number} [toEpoch=Date.now()] - The later timestamp in milliseconds (defaults to now).
 * @returns {string} A string representing how long ago the time was.
 */
function timeAgo(fromEpoch, toEpoch = Date.now()) {
  const diffMs = toEpoch - fromEpoch;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return `${diffSeconds}s`;
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m`;
  } else if (diffHours < 24) {
    return `${diffHours}h`;
  } else {
    return `${diffDays}d`;
  }
}


export default SocialFeed;
export { timeAgo, ProgressComponent, WhoLiked }
